// The WGSL twin of `../speedfield.ts` (R2827).
//
// The same wash from the same figures: the cubic B-spline in four bilinear
// taps of the speed plane, the mean share of the wet nodes, the cold ramp,
// the look and the reading. The tap is `textureSampleLevel` on the plane
// sampler, because a plane read never takes a derivative. A `float[]`
// rides four to a `vec4f` (`layout.ts`), so a stop's u is
// `U.uSfAt[i / 4][i % 4]`.

import { SPEED_ALL_UNIFORMS, RAMP_STOPS } from '../speedfield';
import { PRELUDE_BODY_WGSL } from './prelude';
import { wgslModule } from './module';

/** The fragment half. A function, because `RAMP_STOPS` is read after the
 *  GLSL module has evaluated. */
function speedFragWgsl(): string {
  return `
// layers/speedfield.ts speedTap: the plane at world point p, a cubic
// B-spline in four bilinear taps. R is the summed share, G the wet cover.
fn speedTap(p: vec2f) -> vec2f {
  let coord = p / U.uSfStep;
  let base = floor(coord);
  let f = coord - base;
  let f2 = f * f;
  let f3 = f2 * f;
  let w0 = (1.0 / 6.0) * (-f3 + 3.0 * f2 - 3.0 * f + 1.0);
  let w1 = (1.0 / 6.0) * (3.0 * f3 - 6.0 * f2 + 4.0);
  let w2 = (1.0 / 6.0) * (-3.0 * f3 + 3.0 * f2 + 3.0 * f + 1.0);
  let w3 = (1.0 / 6.0) * f3;
  let s0 = w0 + w1;
  let s1 = w2 + w3;
  let inv = 1.0 / U.uSfSize;
  let t0 = (base - 0.5 + w1 / s0) * inv;
  let t1 = (base + 1.5 + w3 / s1) * inv;
  let lo = mix(textureSampleLevel(uSpeed, sLinear, vec2f(t0.x, t0.y), 0.0).rg,
               textureSampleLevel(uSpeed, sLinear, vec2f(t1.x, t0.y), 0.0).rg, s1.x);
  let hi = mix(textureSampleLevel(uSpeed, sLinear, vec2f(t0.x, t1.y), 0.0).rg,
               textureSampleLevel(uSpeed, sLinear, vec2f(t1.x, t1.y), 0.0).rg, s1.x);
  return mix(lo, hi, s1.y);
}

fn sfAt(i: i32) -> f32 { return U.uSfAt[i / 4][i % 4]; }

// current-field.ts rampAt: the ink and the alpha of the two stops round u.
fn rampOf(u: f32) -> vec4f {
  for (var i: i32 = 1; i < ${RAMP_STOPS}; i++) {
    if (u > sfAt(i)) { continue; }
    let q = (u - sfAt(i - 1)) / max(1e-9, sfAt(i) - sfAt(i - 1));
    return mix(U.uSfRamp[i - 1], U.uSfRamp[i], q);
  }
  return U.uSfRamp[${RAMP_STOPS - 1}];
}

// current-field.ts smooth: the Hermite step of the cards.
fn sfSmooth(e0: f32, e1: f32, v: f32) -> f32 {
  let q = clamp((v - e0) / max(1e-9, e1 - e0), 0.0, 1.0);
  return q * q * (3.0 - 2.0 * q);
}

@fragment fn fs(v: VsOut) -> @location(0) vec4f {
  let s = speedTap(v.vWorld);
  let wet = clamp(s.y, 0.0, 1.0);
  if (wet <= 0.0) { return vec4f(0.0); }
  let u = clamp(s.x / max(s.y, 1e-6) * abs(U.uSfTide), 0.0, 1.0);
  let r = rampOf(u);
  var a = min(1.0, r.a * U.uSfAlpha);
  if (U.uSfMin > 0.0) { a *= sfSmooth(U.uSfMin, U.uSfMin + U.uSfBand, u); }
  a *= U.uSfRead * wet;
  return vec4f(r.rgb * a, a);
}
`;
}

/** The whole speed field module, for the parity suite. */
export function speedProgramWgsl(): string {
  return wgslModule({ uniforms: SPEED_ALL_UNIFORMS, body: speedBodyWgsl() });
}

/** The speed field's BODY: the shared prelude body and the fragment entry. */
export function speedBodyWgsl(): string {
  return PRELUDE_BODY_WGSL + speedFragWgsl();
}
